<template>
  <div class="max-w-7xl mx-auto pb-10 relative">
    <div class="space-y-6 transition-all duration-300 animate-fade-in" :class="{ 'blur-sm pointer-events-none opacity-50': showModal }">

      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
            <BookOpen class="w-6 h-6 text-brand-600" />
            Comprobantes de Diario
          </h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm">Historial de asientos contables y partidas de diario registradas.</p>
        </div>
        <button @click="openCreate" class="flex items-center gap-2 px-5 py-2.5 bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-xl shadow-lg shadow-brand-500/30 transition-all active:scale-95">
          <Plus class="w-5 h-5" /> Nuevo Asiento
        </button>
      </div>

      <div class="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col sm:flex-row gap-4">
        <div class="relative flex-1">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            v-model="searchTerm"
            type="text"
            placeholder="Buscar por concepto o número de referencia..."
            class="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
          >
        </div>
        <input
          v-model="monthFilter"
          type="month"
          class="px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
        />
      </div>

      <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead class="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
              <tr>
                <th class="px-6 py-4 font-semibold">Fecha / Ref</th>
                <th class="px-6 py-4 font-semibold">Concepto General</th>
                <th class="px-6 py-4 font-semibold text-right">Monto Total</th>
                <th class="px-6 py-4 font-semibold text-center">Estado</th>
                <th class="px-6 py-4 font-semibold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800">

              <tr v-if="isLoading">
                <td colspan="5" class="px-6 py-12 text-center text-slate-500">
                  <Loader2 class="w-8 h-8 animate-spin mx-auto mb-3 text-brand-500" /> Cargando asientos contables...
                </td>
              </tr>

              <tr v-else-if="filteredList.length === 0">
                <td colspan="5" class="px-6 py-12 text-center text-slate-500">
                  <BookOpen class="w-8 h-8 mx-auto mb-3 opacity-20" />
                  No se encontraron comprobantes de diario.
                </td>
              </tr>

              <tr v-else v-for="item in filteredList" :key="item.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="font-bold text-slate-900 dark:text-slate-100">{{ formatDate(item.date) }}</div>
                  <div class="text-[10px] font-mono text-slate-500 mt-1 uppercase">
                    REF: {{ item.reference || 'N/A' }}
                  </div>
                </td>
                <td class="px-6 py-4">
                  <div class="text-xs text-slate-700 dark:text-slate-300 max-w-md truncate" :title="item.concept">
                    {{ item.concept }}
                  </div>
                </td>
                <td class="px-6 py-4 text-right whitespace-nowrap">
                  <div class="font-mono font-bold text-slate-900 dark:text-white">
                    ${{ Number(item.total_amount).toLocaleString('en-US', { minimumFractionDigits: 2 }) }}
                  </div>
                </td>
                <td class="px-6 py-4 text-center">
                  <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold border bg-emerald-50 text-emerald-700 border-emerald-200">
                    <CheckCircle class="w-3 h-3" /> Mayorizado
                  </span>
                </td>
                <td class="px-6 py-4 text-right">
                  <button @click="openEdit(item)" class="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title="Editar Asiento">
                    <Edit class="w-5 h-5" />
                  </button>
                </td>
              </tr>

            </tbody>
          </table>
        </div>
      </div>
    </div>

    <JournalEntryForm
      :show="showModal"
      :entryToEdit="selectedEntry"
      @close="showModal = false"
      @saved="loadData"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { Search, Plus, Loader2, BookOpen, Edit, CheckCircle } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import { useToast } from '@/composables/useToast';

// Asegúrate de que la ruta a tu Form.vue es correcta
import JournalEntryForm from './Form.vue';

const list = ref([]);
const isLoading = ref(true);
const searchTerm = ref('');
const monthFilter = ref(''); // Filtro por YYYY-MM
const showModal = ref(false);
const selectedEntry = ref({});
const { showError } = useToast();

// Cargar la lista de asientos contables desde el backend
const loadData = async () => {
  isLoading.value = true;
  try {
    const user = SecureStorage.getItem('llevo_user');
    if (!user?.company) return;

    // Endpoint esperado: GET /acc_journal_entries
    const res = await apiClient.get(`/acc_journal_entries?company_id=${user.company}`);
    list.value = Array.isArray(res.data) ? res.data : (res.data.data || []);
  } catch (e) {
    showError('Error cargando los comprobantes de diario.');
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  // Configurar el mes actual por defecto para el filtro (YYYY-MM)
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  monthFilter.value = `${yyyy}-${mm}`;

  loadData();
});

// Filtro combinado (Buscador por texto + Mes/Año)
const filteredList = computed(() => {
  let result = list.value;

  // 1. Filtrar por texto (concepto o referencia)
  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase();
    result = result.filter(i =>
      (i.concept && i.concept.toLowerCase().includes(term)) ||
      (i.reference && i.reference.toLowerCase().includes(term))
    );
  }

  // 2. Filtrar por mes (YYYY-MM) si está seleccionado
  if (monthFilter.value) {
    result = result.filter(i => i.date && i.date.startsWith(monthFilter.value));
  }

  return result;
});

// Formatear la fecha para que se vea más amigable
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const parts = dateString.split('-'); // asumiendo YYYY-MM-DD
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return dateString;
};

// Acciones para abrir el modal (Form.vue)
const openCreate = () => {
  selectedEntry.value = {};
  showModal.value = true;
};

const openEdit = async (item) => {
  // Opcional: Si el detalle de las líneas no viene en el listado principal,
  // tendrías que hacer un GET by ID aquí para traer los `lines` antes de abrir.
  // Ejemplo:
  // const res = await apiClient.get(`/acc_journal_entries/${item.id}`);
  // selectedEntry.value = res.data;

  // Si el listado ya trae las líneas, puedes pasarlo directamente:
  selectedEntry.value = { ...item };
  showModal.value = true;
};
</script>